import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash, PencilSimple, UserCircle, X, FloppyDisk, CheckCircle, FilePdf, FileXls, MagnifyingGlass } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const ADMIN_STATUSES = [
  { value: "facturado", label: "Facturado", color: "bg-emerald-100 text-emerald-700" },
  { value: "oc", label: "OC", color: "bg-amber-100 text-amber-700" },
  { value: "pagado", label: "Pagado", color: "bg-blue-100 text-blue-700" },
];
const ENG_STATUSES = [
  { value: "instalado", label: "Instalado", color: "bg-emerald-100 text-emerald-700" },
  { value: "enviado", label: "Enviado", color: "bg-violet-100 text-violet-700" },
  { value: "programado", label: "Programado", color: "bg-slate-100 text-slate-700" },
];
const PERIODS = [
  { value: "month", label: "Mes" },
  { value: "biweekly1", label: "1ra Quincena" },
  { value: "biweekly2", label: "2da Quincena" },
  { value: "week", label: "Semana" },
];

export function SalesCommissionsPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("list");
  const [period, setPeriod] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSeller, setFilterSeller] = useState("all");
  const [filterAdminStatus, setFilterAdminStatus] = useState("all");
  const [filterEngStatus, setFilterEngStatus] = useState("all");
  const [form, setForm] = useState({
    equipment_name: "", description: "", seller_id: "", seller_name: "",
    client_name: "", amount: "", commission_percent: "5", date: new Date().toISOString().slice(0, 10),
    admin_status: "facturado", engineering_status: "programado",
  });

  const headers = { Authorization: `Bearer ${token}` };
  const selectedMonth = month || new Date().getMonth() + 1;

  const load = useCallback(async () => {
    try {
      const [rRes, sumRes, uRes] = await Promise.all([
        axios.get(`${API}/admin/sales-commissions`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/admin/sales-commissions/summary`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRecords(rRes.data);
      setSummary(sumRes.data);
      setUsers(uRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, selectedMonth, token]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ equipment_name: "", description: "", seller_id: "", seller_name: "", client_name: "", amount: "", commission_percent: "5", date: new Date().toISOString().slice(0, 10), admin_status: "facturado", engineering_status: "programado" });
    setEditId(null);
  };

  const handleSave = async () => {
    if (!form.equipment_name.trim()) return toast.error("Equipo a comisionar requerido");
    if (!form.seller_id) return toast.error("Selecciona el vendedor");
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0), commission_percent: parseFloat(form.commission_percent || 0) };
      if (editId) {
        await axios.put(`${API}/admin/sales-commissions/${editId}`, payload, { headers });
        toast.success("Comision actualizada");
      } else {
        await axios.post(`${API}/admin/sales-commissions`, payload, { headers });
        toast.success("Comision registrada");
      }
      resetForm(); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleEdit = (r) => {
    setForm({
      equipment_name: r.equipment_name, description: r.description || "",
      seller_id: r.seller_id, seller_name: r.seller_name,
      client_name: r.client_name || "", amount: String(r.amount || ""),
      commission_percent: String(r.commission_percent || "5"),
      date: r.date, admin_status: r.admin_status || "facturado",
      engineering_status: r.engineering_status || "programado",
    });
    setEditId(r.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta comision?")) return;
    try { await axios.delete(`${API}/admin/sales-commissions/${id}`, { headers }); toast.success("Eliminada"); load(); }
    catch { toast.error("Error"); }
  };

  const handleAuthorize = async (id) => {
    if (!window.confirm("Autorizar esta comision? Se registrara en Gastos.")) return;
    try {
      const res = await axios.post(`${API}/admin/sales-commissions/${id}/authorize`, {}, { headers });
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al autorizar"); }
  };

  const handleSellerSelect = (uid) => {
    const u = users.find(u => u.id === uid);
    setForm({ ...form, seller_id: uid, seller_name: u?.name || "" });
  };

  const filterByPeriod = (items) => {
    if (period === "month") return items;
    const today = new Date();
    return items.filter(r => {
      const d = new Date(r.date + "T12:00:00");
      const day = d.getDate();
      if (period === "biweekly1") return day <= 15;
      if (period === "biweekly2") return day > 15;
      if (period === "week") {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return d >= startOfWeek && d <= endOfWeek;
      }
      return true;
    });
  };

  const filtered = filterByPeriod(records).filter(r => {
    const matchSearch = !searchTerm || (r.equipment_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (r.client_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (r.seller_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchSeller = filterSeller === "all" || r.seller_id === filterSeller;
    const matchAdmin = filterAdminStatus === "all" || r.admin_status === filterAdminStatus;
    const matchEng = filterEngStatus === "all" || r.engineering_status === filterEngStatus;
    return matchSearch && matchSeller && matchAdmin && matchEng;
  });

  const totalGlobal = filtered.reduce((acc, r) => acc + (r.amount || 0), 0);
  const totalCommission = filtered.reduce((acc, r) => acc + (r.commission_amount || 0), 0);

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const sellers = users.filter(u => u.role === "comercial");

  return (
    <div className="space-y-4" data-testid="sales-commissions-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-slate-700">Comisiones Vendedores — {MONTHS[selectedMonth - 1]} {year}</h2>
          {summary && (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {summary.total_records} registros | {fmt(summary.total_commission)} comisiones
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab("list")} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${tab === "list" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="sc-tab-list">Registros</button>
          <button onClick={() => setTab("summary")} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${tab === "summary" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="sc-tab-summary">Resumen</button>
          <button className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="sc-pdf-btn"><FilePdf size={14} /> PDF</button>
          <button className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="sc-excel-btn"><FileXls size={14} /> Excel</button>
          {isAdmin && <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-1.5" data-testid="add-sales-comm-btn"><Plus size={16} /> Registrar</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-3 items-center" data-testid="sc-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-8 text-xs" placeholder="Buscar equipo, cliente, vendedor..." data-testid="sc-search" />
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="sc-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterSeller} onValueChange={setFilterSeller}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="sc-filter-seller"><SelectValue placeholder="Vendedor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {sellers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAdminStatus} onValueChange={setFilterAdminStatus}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="sc-filter-admin"><SelectValue placeholder="Est. Admin" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {ADMIN_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterEngStatus} onValueChange={setFilterEngStatus}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="sc-filter-eng"><SelectValue placeholder="Est. Ing." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {ENG_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Global</p><p className="text-lg font-bold text-slate-900">{fmt(totalGlobal)}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Comisiones</p><p className="text-lg font-bold text-teal-700">{fmt(totalCommission)}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Registros</p><p className="text-lg font-bold text-blue-700">{filtered.length}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Autorizados</p><p className="text-lg font-bold text-amber-700">{filtered.filter(r => r.authorized).length}/{filtered.length}</p></div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="sc-form">
          <h3 className="font-semibold text-slate-700">{editId ? "Editar Comision" : "Registrar Comision"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-xs">Equipo a Comisionar *</Label><Input value={form.equipment_name} onChange={e => setForm({ ...form, equipment_name: e.target.value })} className="mt-1 h-8 text-xs" data-testid="sc-equip-input" /></div>
            <div><Label className="text-xs">Vendido por *</Label>
              <select value={form.seller_id} onChange={e => handleSellerSelect(e.target.value)} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="sc-seller-select">
                <option value="">Seleccionar...</option>
                {users.filter(u => ["comercial", "admin"].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Cliente</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1 h-8 text-xs" data-testid="sc-client-input" /></div>
            <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 h-8 text-xs" data-testid="sc-date-input" /></div>
            <div><Label className="text-xs">Monto Total ($)</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1 h-8 text-xs" data-testid="sc-amount-input" /></div>
            <div><Label className="text-xs">% Comision</Label><Input type="number" value={form.commission_percent} onChange={e => setForm({ ...form, commission_percent: e.target.value })} className="mt-1 h-8 text-xs" data-testid="sc-commission-input" /></div>
            <div><Label className="text-xs">Estatus Admin</Label>
              <select value={form.admin_status} onChange={e => setForm({ ...form, admin_status: e.target.value })} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="sc-admin-status">
                {ADMIN_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Estatus Ingenieria</Label>
              <select value={form.engineering_status} onChange={e => setForm({ ...form, engineering_status: e.target.value })} className="mt-1 w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="sc-eng-status">
                {ENG_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs">Descripcion</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 h-8 text-xs" /></div>
          {form.amount && form.commission_percent && (
            <p className="text-xs text-teal-700 font-medium bg-teal-50 px-3 py-1.5 rounded">Comision calculada: {fmt(parseFloat(form.amount || 0) * parseFloat(form.commission_percent || 0) / 100)}</p>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 flex items-center gap-1.5" data-testid="sc-save-btn"><FloppyDisk size={14} /> {editId ? "Actualizar" : "Guardar"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 flex items-center gap-1.5"><X size={14} /> Cancelar</button>
          </div>
        </div>
      )}

      {/* List */}
      {tab === "list" && (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-xs" data-testid="sc-table">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="py-2.5 px-2 text-left font-semibold">Fecha</th>
                <th className="py-2.5 px-2 text-left font-semibold">Equipo</th>
                <th className="py-2.5 px-2 text-left font-semibold">Vendido por</th>
                <th className="py-2.5 px-2 text-left font-semibold">Cliente</th>
                <th className="py-2.5 px-2 text-right font-semibold">Monto</th>
                <th className="py-2.5 px-2 text-center font-semibold">%</th>
                <th className="py-2.5 px-2 text-right font-semibold">Comision</th>
                <th className="py-2.5 px-2 text-center font-semibold">Est. Admin</th>
                <th className="py-2.5 px-2 text-center font-semibold">Est. Ing.</th>
                <th className="py-2.5 px-2 text-center font-semibold">Autorizado</th>
                <th className="py-2.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="py-10 text-center text-slate-400">Sin registros</td></tr>
              ) : filtered.map(r => {
                const adminSt = ADMIN_STATUSES.find(a => a.value === r.admin_status);
                const engSt = ENG_STATUSES.find(e => e.value === r.engineering_status);
                return (
                  <tr key={r.id} className="border-t hover:bg-slate-50/70" data-testid={`sc-row-${r.id}`}>
                    <td className="py-2 px-2 text-slate-600 font-mono">{r.date}</td>
                    <td className="py-2 px-2 font-medium text-slate-800">{r.equipment_name}</td>
                    <td className="py-2 px-2"><span className="inline-flex items-center gap-1 text-teal-700 font-medium"><UserCircle size={13} /> {r.seller_name || "\u2014"}</span></td>
                    <td className="py-2 px-2 text-slate-600">{r.client_name || "\u2014"}</td>
                    <td className="py-2 px-2 text-right font-semibold text-slate-800">{fmt(r.amount)}</td>
                    <td className="py-2 px-2 text-center text-slate-600">{r.commission_percent || 0}%</td>
                    <td className="py-2 px-2 text-right font-semibold text-teal-700">{fmt(r.commission_amount)}</td>
                    <td className="py-2 px-2 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${adminSt?.color || "bg-slate-100"}`}>{adminSt?.label || r.admin_status}</span></td>
                    <td className="py-2 px-2 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${engSt?.color || "bg-slate-100"}`}>{engSt?.label || r.engineering_status}</span></td>
                    <td className="py-2 px-2 text-center">
                      {r.authorized ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Autorizado</span>
                      ) : isAdmin ? (
                        <button onClick={() => handleAuthorize(r.id)} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors" data-testid={`sc-auth-${r.id}`}>Autorizar</button>
                      ) : (
                        <span className="text-[10px] text-slate-400">Pendiente</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-0.5">
                        {isAdmin && <button onClick={() => handleEdit(r)} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`sc-edit-${r.id}`}><PencilSimple size={13} /></button>}
                        {isAdmin && <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`sc-del-${r.id}`}><Trash size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-xs">
                  <td colSpan={4} className="py-2.5 px-2 text-right">TOTAL GLOBAL:</td>
                  <td className="py-2.5 px-2 text-right text-slate-900">{fmt(totalGlobal)}</td>
                  <td></td>
                  <td className="py-2.5 px-2 text-right text-teal-700">{fmt(totalCommission)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Summary */}
      {tab === "summary" && summary && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 bg-slate-50 border-b"><h3 className="text-sm font-semibold text-slate-700">Total por Vendedor</h3></div>
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-100">
                <th className="py-2 px-3 text-left font-semibold text-slate-600">Vendedor</th>
                <th className="py-2 px-3 text-center font-semibold text-slate-600">Ventas</th>
                <th className="py-2 px-3 text-right font-semibold text-slate-600">Monto</th>
                <th className="py-2 px-3 text-right font-semibold text-slate-600">Comision</th>
              </tr></thead>
              <tbody>
                {summary.by_seller?.map(u => (
                  <tr key={u.user_id} className="border-t hover:bg-slate-50/70">
                    <td className="py-2.5 px-3 font-medium text-slate-800 flex items-center gap-1.5"><UserCircle size={16} className="text-teal-500" /> {u.user_name}</td>
                    <td className="py-2.5 px-3 text-center font-semibold">{u.count}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-slate-700">{fmt(u.total_amount)}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-teal-700">{fmt(u.total_commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
